/**
 * Countdown Timer Component (Task 12)
 * 24 hours from first visit (stored per user in localStorage).
 * When expired: shows "Offer expired — price has increased to ₹499"
 * Config: localStorage key "pos_offer_expiry"
 */
import React, { useState, useEffect } from "react";
import { Clock } from "lucide-react";

const STORAGE_KEY = "pos_offer_expiry";
const OFFER_DURATION_HOURS = 24; // from .env or hardcoded

function getExpiryTime() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const expiry = parseInt(stored, 10);
    if (!isNaN(expiry) && expiry > Date.now()) {
      return expiry;
    }
  }
  // First visit - set expiry to 24 hours from now
  const newExpiry = Date.now() + OFFER_DURATION_HOURS * 60 * 60 * 1000;
  localStorage.setItem(STORAGE_KEY, newExpiry.toString());
  return newExpiry;
}

function formatTime(ms) {
  if (ms <= 0) return { h: "00", m: "00", s: "00" };
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return {
    h: String(h).padStart(2, "0"),
    m: String(m).padStart(2, "0"),
    s: String(s).padStart(2, "0"),
  };
}

export default function CountdownTimer({ onExpired }) {
  const [expiryTime] = useState(() => getExpiryTime());
  const [remaining, setRemaining] = useState(expiryTime - Date.now());
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const tick = () => {
      const r = expiryTime - Date.now();
      if (r <= 0) {
        setRemaining(0);
        setExpired(true);
        if (onExpired) onExpired();
      } else {
        setRemaining(r);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiryTime, onExpired]);

  if (expired) {
    return (
      <div className="inline-flex items-center gap-2 bg-[var(--ap-canvas)] border border-[var(--ap-line)] rounded-lg px-4 py-2">
        <span className="text-[var(--ap-muted)] text-sm font-medium">
          Offer expired — price has increased to ₹499
        </span>
      </div>
    );
  }

  const { h, m, s } = formatTime(remaining);

  return (
    <div className="inline-flex items-center gap-2">
      <Clock className="w-4 h-4 text-[var(--ap-orange)]" />
      <span className="text-sm text-[var(--ap-muted)] font-medium">Offer ends in:</span>
      <div className="flex items-center gap-1">
        {[h, m, s].map((unit, i) => (
          <React.Fragment key={i}>
            <div className="bg-[var(--ap-ink)] text-white px-2 py-1 rounded text-sm font-mono font-bold min-w-[32px] text-center">
              {unit}
            </div>
            {i < 2 && <span className="text-[var(--ap-muted)] font-bold text-sm">:</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
